import { z } from 'zod';
import { costCategorySchema, equationKindSchema, equationScopeSchema, versionStatusSchema } from '../../common/schemas/enum.schemas.js';
import { uuidSchema } from '../../common/schemas/id.schemas.js';
import { revisionSchema } from '../../common/schemas/money.schemas.js';
/**
 * Settings owns the equations; templates own the shared values they read (plan §6, PP §6).
 *
 * These schemas mirror the engine's types deliberately rather than importing them: the contracts
 * package is the wire format and must stay free of the engine, and the engine must stay free of zod.
 * `policies.spec` on the service side pins the two together, and the unit list below is the engine's
 * `UNIT_NAMES` — a unit the dimensional checker does not know would be rejected at compile time anyway,
 * but rejecting it at the edge gives a better message.
 */
export const equationUnitSchema = z.enum(['JOD', 'km', 'L', 'days', 'years', 'fraction', 'count', 'flag', 'JOD/day', 'JOD/L', 'km/year']);
/** The five sections the Equations page draws, in display order. */
export const equationGroupSchema = z.enum(['AGE_VALUE', 'INSPECTION', 'COSTS', 'ACQUISITION', 'TRADE_IN']);
/** A SYSTEM id is fixed; a CUSTOM id is an `x_` intermediate an administrator adds (plan §5.1). */
export const equationIdSchema = z
    .string()
    .trim()
    .min(1, 'An equation needs an id')
    .max(64)
    .regex(/^[A-Za-z_][A-Za-z0-9_]*$/, 'An id may contain only letters, digits and underscores, and cannot start with a digit');
export const equationDefSchema = z.object({
    id: equationIdSchema,
    kind: equationKindSchema,
    scope: equationScopeSchema,
    label: z.string().trim().min(1, 'Every equation needs a label').max(120),
    unit: equationUnitSchema,
    group: equationGroupSchema,
    expression: z.string().trim().min(1, 'An equation cannot be empty').max(1_600, 'An expression may be at most 1,600 characters'),
    /** Set on the equations whose estimate the recorded cost items of that category replace (plan §5.5). */
    costCategory: costCategorySchema.optional(),
    description: z.string().max(2_000).optional(),
});
export const equationListSchema = z.array(equationDefSchema).min(1, 'A policy needs at least one equation').max(200);
/** One problem found by the engine's compiler, addressed to the equation it belongs to. */
export const policyErrorSchema = z.object({
    code: z.enum([
        'SYNTAX',
        'UNKNOWN_FUNCTION',
        'UNKNOWN_REFERENCE',
        'CIRCULAR',
        'UNIT_MISMATCH',
        'SCOPE_LEAK',
        'RESERVED_ID',
        'MISSING_SYSTEM_OUTPUT',
        'DIVISION_BY_ZERO',
        'NON_FINITE',
        'MISSING_INPUT',
        'ARITY',
    ]),
    equationId: z.string().optional(),
    /** The equation's label, so the page can name it without looking it up. */
    label: z.string().optional(),
    detail: z.string(),
    position: z.number().int().min(0).optional(),
    token: z.string().optional(),
});
/** A row of the versions table: everything except the equations themselves. */
export const policyVersionSummarySchema = z.object({
    id: uuidSchema,
    version: z.number().int().min(1),
    status: versionStatusSchema,
    reason: z.string().nullable(),
    publishedAt: z.string().datetime().nullable(),
    publishedBy: uuidSchema.nullable(),
    publishedByName: z.string().nullable(),
    createdBy: uuidSchema.nullable(),
    equationCount: z.number().int().min(0),
    revision: revisionSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const policyVersionSchema = policyVersionSummarySchema.extend({
    equations: z.array(equationDefSchema),
});
export const listPolicyVersionsOutputSchema = z.array(policyVersionSummarySchema);
export const getPolicyVersionInputSchema = z.object({
    versionId: uuidSchema,
});
export const createPolicyDraftInputSchema = z.object({
    /** Copy the equations of this version; defaults to the published one (plan §7 "Open as draft"). */
    fromVersionId: uuidSchema.optional(),
});
export const updatePolicyDraftInputSchema = z.object({
    versionId: uuidSchema,
    expectedRevision: revisionSchema,
    equations: equationListSchema,
    reason: z.string().trim().max(2_000),
});
export const validatePolicyDraftInputSchema = z.object({
    equations: equationListSchema,
});
export const validatePolicyDraftOutputSchema = z.object({
    ok: z.boolean(),
    errors: z.array(policyErrorSchema),
    /** A stable hash of the equations that were validated, so publishing can insist on the same ones. */
    fingerprint: z.string(),
});
/**
 * `NEW` leaves every existing record where it is: a published policy applies to new valuations and
 * saved snapshots keep the version they were computed with (PP AC08, AC14). `OPEN_ESTIMATES`
 * additionally re-points the estimates that are still open — `DRAFT`, `READY_TO_OFFER` and
 * `OFFER_SHARED` — at the new version and records an activity row on each. **Closed estimates are never
 * touched**, because a deal that was agreed on one policy must keep saying so.
 */
export const publishPolicyScopeSchema = z.enum(['NEW', 'OPEN_ESTIMATES']);
export const publishPolicyInputSchema = z.object({
    versionId: uuidSchema,
    expectedRevision: revisionSchema,
    reason: z.string().trim().min(1, 'Explain the intended change'),
    scope: publishPolicyScopeSchema.default('NEW'),
});
/**
 * The impact preview (plan §6, PP AC08): what these equations would do to **every open estimate**,
 * before anything is published. Each row is priced with that estimate's own template version and
 * inspection, so the comparison is like for like; an estimate whose facts are not complete enough to
 * price is listed as `incomplete` rather than silently dropped.
 */
export const impactFiguresSchema = z.object({
    age: z.number().nullable(),
    appraisal: z.number().nullable(),
    ceiling: z.number().nullable(),
    insurance: z.number().nullable(),
});
/**
 * Why a row could not be priced (plan §11 review fix 15). `MISSING_INPUTS` is a fact nobody has
 * recorded, `BLOCKING_FACTS` a value that is invalid rather than absent, `EVALUATION_ERROR` an equation
 * the engine could not run, `INVALID_OUTPUTS` a run whose figures `validateOutputs` rejected.
 */
export const impactIncompleteReasonSchema = z.enum(['MISSING_INPUTS', 'BLOCKING_FACTS', 'EVALUATION_ERROR', 'INVALID_OUTPUTS']);
export const impactRowSchema = z.object({
    estimateId: uuidSchema,
    estimateNo: z.string(),
    status: z.string(),
    customerName: z.string().nullable(),
    vehicle: z.string(),
    templateName: z.string().nullable(),
    /** The estimate could not be priced at all; the figures are whatever the run managed. */
    incomplete: z.boolean(),
    /** Why, when `incomplete`; `null` otherwise. */
    reason: impactIncompleteReasonSchema.nullable(),
    before: impactFiguresSchema,
    after: impactFiguresSchema,
    delta: impactFiguresSchema,
});
export const impactPreviewInputSchema = z.object({
    equations: equationListSchema,
});
export const impactPreviewOutputSchema = z.object({
    publishedVersion: z.number().int().min(1).nullable(),
    rows: z.array(impactRowSchema),
    /** How many of the open estimates the candidate could not price — the publish dialog says so. */
    incompleteCount: z.number().int().min(0),
});
/** One available input, for the "Available inputs" panel and the estimator's provenance badges. */
export const policyInputSchema = z.object({
    name: z.string(),
    unit: equationUnitSchema,
    source: z.enum(['VEHICLE', 'TEMPLATE', 'OVERRIDABLE', 'SYSTEM', 'TRADE_IN']),
    label: z.string(),
    description: z.string(),
});
export const policyInputCatalogOutputSchema = z.array(policyInputSchema);
//# sourceMappingURL=policies.schemas.js.map